import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Button,
  Input,
  Select,
  FormField,
  ColumnLayout,
  DateRangePicker,
  Modal,
  Alert
} from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';
import { apiService } from '../services/api';

interface AuditLogEntry {
  logId: string;
  timestamp: string;
  adminEmail: string;
  action: string;
  targetUser: string;
  details: string;
  oldValue?: string;
  newValue?: string;
}

export const AuditLog: React.FC = () => {
  const { isAdmin, isLoading: roleLoading } = useRole();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLogEntry[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // Filters
  const [dateRange, setDateRange] = useState<any>(null);
  const [adminFilter, setAdminFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [targetUserFilter, setTargetUserFilter] = useState<string>('');

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, dateRange, adminFilter, actionFilter, targetUserFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      
      const response = await apiService.getAuditLogs();
      
      if (response.data?.status === 'success') {
        const apiLogs = (response.data.logs || []).map((log: any) => ({
          logId: log.log_id || log.timestamp || '',
          timestamp: log.timestamp || '',
          adminEmail: log.admin_email || '',
          action: log.action || '',
          targetUser: log.target_user_id || '',
          details: typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || ''),
          oldValue: log.details?.old_value || '',
          newValue: log.details?.new_value || '',
        }));
        setLogs(apiLogs);
      } else {
        console.error('Error loading audit logs:', response.data?.error || response.error);
        setLogs([]);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];
    
    // Apply date range filter
    if (dateRange?.startDate && dateRange?.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= start && logDate <= end;
      });
    }
    
    // Apply admin filter
    if (adminFilter) {
      filtered = filtered.filter(log => 
        log.adminEmail.toLowerCase().includes(adminFilter.toLowerCase())
      );
    }
    
    // Apply action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }
    
    // Apply target user filter
    if (targetUserFilter) {
      filtered = filtered.filter(log => 
        log.targetUser.toLowerCase().includes(targetUserFilter.toLowerCase())
      );
    }
    
    setFilteredLogs(filtered);
  };

  const handleViewDetails = (log: AuditLogEntry) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleExportToCSV = async () => {
    try {
      const response = await apiService.exportAuditLogs();
      
      if (response.data?.status === 'success' && response.data.csv_data) {
        const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-log-${new Date().toISOString()}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Error exporting audit logs:', response.data?.error || response.error);
      }
    } catch (error) {
      console.error('Error exporting audit logs:', error);
    }
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      role_change: 'Role Change',
      resource_access: 'Resource Access',
      resource_creation: 'Resource Creation',
      resource_deletion: 'Resource Deletion',
      resource_modification: 'Resource Modification'
    };
    return labels[action] || action;
  };

  // Check if user has permission
  if (!roleLoading && !isAdmin) {
    return (
      <Container>
        <Alert type="error">
          You do not have permission to access the audit log.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="View administrative actions and system changes"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={loadLogs} iconName="refresh">
              Refresh
            </Button>
            <Button onClick={handleExportToCSV} iconName="download">
              Export to CSV
            </Button>
          </SpaceBetween>
        }
      >
        Audit Log
      </Header>
      
      <Container>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Date Range">
              <DateRangePicker
                value={dateRange}
                onChange={({ detail }) => setDateRange(detail.value)}
                placeholder="Filter by date range"
                relativeOptions={[
                  { key: 'previous-7-days', amount: 7, unit: 'day', type: 'relative' },
                  { key: 'previous-30-days', amount: 30, unit: 'day', type: 'relative' },
                  { key: 'previous-90-days', amount: 90, unit: 'day', type: 'relative' }
                ]}
                isValidRange={() => ({ valid: true })}
              />
            </FormField>
            
            <FormField label="Admin User">
              <Input
                value={adminFilter}
                onChange={({ detail }) => setAdminFilter(detail.value)}
                placeholder="Filter by admin email..."
              />
            </FormField>
          </ColumnLayout>
          
          <ColumnLayout columns={2}>
            <FormField label="Action Type">
              <Select
                selectedOption={{ label: actionFilter === 'all' ? 'All Actions' : getActionLabel(actionFilter), value: actionFilter }}
                onChange={({ detail }) => setActionFilter(detail.selectedOption.value || 'all')}
                options={[
                  { label: 'All Actions', value: 'all' },
                  { label: 'Role Change', value: 'role_change' },
                  { label: 'Resource Access', value: 'resource_access' },
                  { label: 'Resource Creation', value: 'resource_creation' },
                  { label: 'Resource Deletion', value: 'resource_deletion' },
                  { label: 'Resource Modification', value: 'resource_modification' }
                ]}
              />
            </FormField>
            
            <FormField label="Target User">
              <Input
                value={targetUserFilter}
                onChange={({ detail }) => setTargetUserFilter(detail.value)}
                placeholder="Filter by target user email..."
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'timestamp',
              header: 'Timestamp',
              cell: (item: AuditLogEntry) => new Date(item.timestamp).toLocaleString(),
              sortingField: 'timestamp'
            },
            {
              id: 'adminEmail',
              header: 'Admin',
              cell: (item: AuditLogEntry) => item.adminEmail,
              sortingField: 'adminEmail'
            },
            {
              id: 'action',
              header: 'Action',
              cell: (item: AuditLogEntry) => getActionLabel(item.action),
              sortingField: 'action'
            },
            {
              id: 'targetUser',
              header: 'Target User',
              cell: (item: AuditLogEntry) => item.targetUser,
              sortingField: 'targetUser'
            },
            {
              id: 'details',
              header: 'Details',
              cell: (item: AuditLogEntry) => item.details
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: AuditLogEntry) => (
                <Button onClick={() => handleViewDetails(item)}>
                  View Details
                </Button>
              )
            }
          ]}
          items={filteredLogs}
          loading={loading}
          loadingText="Loading audit logs..."
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No audit logs</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No audit logs match the current filters.
              </Box>
            </Box>
          }
        />
      </Container>
      
      {/* Log Details Modal */}
      <Modal
        visible={showModal}
        onDismiss={() => setShowModal(false)}
        header="Audit Log Details"
        size="medium"
      >
        {selectedLog && (
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Log ID</Box>
              <div>{selectedLog.logId}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Timestamp</Box>
              <div>{new Date(selectedLog.timestamp).toLocaleString()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Admin User</Box>
              <div>{selectedLog.adminEmail}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Action Type</Box>
              <div>{getActionLabel(selectedLog.action)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Target User</Box>
              <div>{selectedLog.targetUser}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Details</Box>
              <div>{selectedLog.details}</div>
            </div>
            {selectedLog.oldValue && (
              <div>
                <Box variant="awsui-key-label">Old Value</Box>
                <div>{selectedLog.oldValue}</div>
              </div>
            )}
            {selectedLog.newValue && (
              <div>
                <Box variant="awsui-key-label">New Value</Box>
                <div>{selectedLog.newValue}</div>
              </div>
            )}
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
};

export default AuditLog;
